import React, { useMemo } from 'react';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { COMPANY_BRAND_ORANGE } from '../../constants/companyBrand';
import { BRAND_LUXE_PRIMARY, BRAND_MODAL_ACCENT_ICON } from '../../pages/BrandDashboard/brandDashboardTokens';
import { normalizeRichTextForEditor } from '../../pages/BrandDashboard/contestWizard/richTextUtils';
import type { RichTextEditorProps } from './richText/types';

export type { RichTextEditorShellVariant, RichTextEditorProps } from './richText/types';

const defaultToolbarModules = {
    toolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ color: [] }, { background: [] }],
        ['link', 'image'],
        ['clean'],
    ],
};

const companyAboutToolbarModules = {
    toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link']],
};

/** Matches `defaultToolbarModules` so every control stays enabled. */
const fullEditorFormats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'color',
    'background',
    'link',
    'image',
];

export const QuillRichTextEditor: React.FC<RichTextEditorProps> = ({
    value,
    onChange,
    labelSx,
    label,
    placeholder,
    minChar,
    shellVariant = 'default',
    compact = false,
    fillContainer = false,
}) => {
    const { themeStyle } = useThemeContext();

    const modules = useMemo(() => {
        if (shellVariant === 'companyAbout') {
            return companyAboutToolbarModules;
        }
        return defaultToolbarModules;
    }, [shellVariant]);

    const formats = useMemo(() => {
        if (shellVariant === 'companyAbout') {
            return ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];
        }
        return fullEditorFormats;
    }, [shellVariant]);

    const editorValue = useMemo(() => normalizeRichTextForEditor(value), [value]);

    // Calculate character count (strip HTML tags for accurate count)
    const getTextContent = (html: string) => {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent || div.innerText || '';
    };

    const characterCount = getTextContent(editorValue).length;
    const isMinCharMet = minChar ? characterCount >= minChar : true;

    const defaultShellSx = {
        position: 'relative' as const,
        width: '100%',
        '& .ql-container': {
            backgroundColor: theme[themeStyle].secondaryBg,
            color: theme[themeStyle].primary_white,
            border: `1px solid ${theme[themeStyle].main_blue}`,
            borderTop: 'none',
            borderRadius: '0 0 8px 8px',
        },
        '& .ql-toolbar': {
            backgroundColor: theme[themeStyle].secondaryBg,
            border: `1px solid ${theme[themeStyle].highlight}`,
            borderBottom: 'none',
            borderRadius: '8px 8px 0 0',
            '& .ql-picker': {
                color: theme[themeStyle].primary_white,
            },
            '& .ql-stroke': {
                stroke: theme[themeStyle].primary_white,
            },
            '& .ql-fill': {
                fill: theme[themeStyle].primary_white,
            },
        },
    };

    /** Compact single-row toolbar (company about). */
    const compactBrandToolbarSx = {
        backgroundColor: '#262626',
        border: 'none',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 0,
        padding: '6px 10px',
        lineHeight: 1,
        boxSizing: 'border-box',
        display: 'flex',
        flexWrap: 'nowrap' as const,
        alignItems: 'center',
        gap: 0,
        '& .ql-formats': {
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
            marginRight: 10,
            paddingRight: 10,
            borderRight: '1px solid rgba(255,255,255,0.12)',
            verticalAlign: 'middle',
            '&:last-of-type': {
                marginRight: 0,
                paddingRight: 0,
                borderRight: 'none',
            },
        },
        '& .ql-picker-label': { color: '#ffffff' },
        '& .ql-picker-options': { backgroundColor: '#262626' },
        '& .ql-stroke': { stroke: '#ffffff' },
        '& .ql-fill': { fill: '#ffffff' },
        '& button:hover .ql-stroke, & button.ql-active .ql-stroke': { stroke: COMPANY_BRAND_ORANGE },
        '& button:hover .ql-fill, & button.ql-active .ql-fill': { fill: COMPANY_BRAND_ORANGE },
        '& button': {
            float: 'none',
            width: 28,
            height: 28,
            minHeight: 28,
            padding: 0,
            lineHeight: 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        '& button svg': {
            width: 18,
            height: 18,
        },
    };

    /** Full toolbar for brand dashboard — all Quill controls, aligned multi-row wrap. */
    const fullBrandToolbarSx = {
        backgroundColor: '#262626',
        border: 'none',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 0,
        padding: '8px 10px',
        lineHeight: 1,
        boxSizing: 'border-box',
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'center',
        alignContent: 'flex-start',
        rowGap: '6px',
        columnGap: '2px',
        '& .ql-formats': {
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
            margin: 0,
            marginRight: 8,
            paddingRight: 8,
            borderRight: '1px solid rgba(255,255,255,0.12)',
            float: 'none',
            verticalAlign: 'middle',
            '&:last-of-type': {
                marginRight: 0,
                paddingRight: 0,
                borderRight: 'none',
            },
        },
        '& .ql-picker': {
            color: '#ffffff',
            height: 28,
            lineHeight: '28px',
        },
        '& .ql-picker-label': {
            color: '#ffffff',
            padding: '0 6px',
            fontSize: 13,
            lineHeight: '28px',
        },
        '& .ql-picker-options': {
            backgroundColor: '#262626',
            border: '1px solid rgba(255,255,255,0.12)',
            maxHeight: 280,
            zIndex: 20,
        },
        '& .ql-picker.ql-expanded .ql-picker-options': {
            marginTop: 4,
            zIndex: 20,
        },
        '& .ql-picker-item': {
            color: '#e8e8e8',
        },
        '& .ql-picker-item:hover, & .ql-picker-item.ql-selected': {
            color: BRAND_MODAL_ACCENT_ICON,
        },
        '& .ql-color .ql-picker-item, & .ql-background .ql-picker-item': {
            borderColor: 'rgba(255,255,255,0.15)',
        },
        '& .ql-stroke': { stroke: '#ffffff' },
        '& .ql-fill': { fill: '#ffffff' },
        '& button:hover .ql-stroke, & button.ql-active .ql-stroke': { stroke: BRAND_MODAL_ACCENT_ICON },
        '& button:hover .ql-fill, & button.ql-active .ql-fill': { fill: BRAND_MODAL_ACCENT_ICON },
        '& button': {
            float: 'none',
            width: 28,
            height: 28,
            minHeight: 28,
            padding: 0,
            lineHeight: 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        '& button svg': {
            width: 18,
            height: 18,
        },
    };

    const companyAboutShellSxBase = {
        position: 'relative' as const,
        width: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.08)',
        '& .ql-toolbar.ql-snow': compactBrandToolbarSx,
        '& .ql-container.ql-snow': {
            backgroundColor: '#000000',
            color: '#ffffff',
            border: 'none',
            borderRadius: 0,
            minHeight: 280,
            fontFamily: FONT_ENUM.MEDIUM_TEXT,
        },
        '& .ql-editor': {
            minHeight: 260,
            /* Single-weight custom body fonts don't faux-bold; map Quill <strong>/<b> to a real bold face */
            '& strong, & b': {
                fontFamily: FONT_ENUM.MAIN_BOLD,
                fontWeight: 700,
            },
            '& em, & i': {
                fontStyle: 'italic',
            },
        },
        '& .ql-editor.ql-blank::before': {
            color: 'rgba(255,255,255,0.35)',
            fontStyle: 'normal',
        },
    };

    /** Brand dashboard: full toolbar + aligned wrap + full border. */
    const brandDashboardShellSx = {
        position: 'relative' as const,
        width: '100%',
        borderRadius: '8px',
        overflow: 'visible',
        border: '1px solid rgba(255,255,255,0.1)',
        boxSizing: 'border-box',
        '&:focus-within': {
            borderColor: `${BRAND_LUXE_PRIMARY}88`,
            boxShadow: '0 0 0 1px rgba(236,170,0,0.25)',
        },
        '& .ql-toolbar.ql-snow': {
            ...fullBrandToolbarSx,
            borderRadius: '8px 8px 0 0',
            position: 'relative',
            zIndex: 2,
            overflow: 'visible',
        },
        '& .ql-container.ql-snow': {
            backgroundColor: '#262626',
            color: '#ffffff',
            border: 'none',
            borderRadius: '0 0 8px 8px',
            minHeight: 280,
            height: 'auto',
            fontFamily: FONT_ENUM.MEDIUM_TEXT,
            overflow: 'visible',
        },
        '& .ql-editor': {
            minHeight: 260,
            height: 'auto',
            padding: '12px 14px',
            overflowY: 'auto',
            '& strong, & b': {
                fontFamily: FONT_ENUM.MAIN_BOLD,
                fontWeight: 700,
            },
            '& em, & i': {
                fontStyle: 'italic',
            },
        },
        '& .ql-editor.ql-blank::before': {
            color: 'rgba(255,255,255,0.35)',
            fontStyle: 'normal',
            left: '12px',
            right: '12px',
        },
    };

    const juryDutyEditorSx: SxProps<Theme> = {
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        borderRadius: '12px 12px 0 0',
        '& .ql-toolbar.ql-snow': {
            backgroundColor: '#1A1A1A',
            border: 'none',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 0,
            '& .ql-picker-label': { color: '#e5e2e1' },
            '& .ql-stroke': { stroke: '#e5e2e1' },
            '& .ql-fill': { fill: '#e5e2e1' },
            '& button:hover .ql-stroke, & button.ql-active .ql-stroke': { stroke: BRAND_LUXE_PRIMARY },
            '& button:hover .ql-fill, & button.ql-active .ql-fill': { fill: BRAND_LUXE_PRIMARY },
        },
        '& .ql-container.ql-snow': {
            backgroundColor: '#121212',
            color: '#e5e2e1',
            border: 'none',
            borderRadius: 0,
            ...(fillContainer && {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
            }),
        },
        ...(fillContainer && {
            display: 'flex',
            flexDirection: 'column',
            '& .quill': {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
            },
        }),
        '& .ql-editor': {
            minHeight: fillContainer ? 48 : compact ? 72 : 160,
            ...(fillContainer && { maxHeight: 'none', flex: 1, overflowY: 'auto' }),
            ...(compact && !fillContainer && { maxHeight: 72, overflowY: 'auto' }),
            '&.ql-blank::before': {
                color: 'rgba(255,255,255,0.35)',
                fontStyle: 'normal',
            },
        },
    };

    const juryDutyShellSx: SxProps<Theme> = {
        position: 'relative',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        bgcolor: '#1A1A1A',
        ...(fillContainer
            ? { height: '100%', minHeight: 0, maxHeight: '100%', flex: 1 }
            : compact
            ? { minHeight: 148, maxHeight: 148 }
            : { minHeight: 220 }),
    };

    const companyAboutShellSx = companyAboutShellSxBase;

    const shellSx =
        shellVariant === 'brandDashboard'
            ? brandDashboardShellSx
            : shellVariant === 'companyAbout'
            ? companyAboutShellSx
            : shellVariant === 'juryDuty'
            ? juryDutyShellSx
            : defaultShellSx;

    const isJuryDuty = shellVariant === 'juryDuty';

    return (
        <Box sx={isJuryDuty ? juryDutyShellSx : shellSx}>
            {!!label && (
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: theme[themeStyle].tag_white,
                        fontSize: 12,
                        mb: shellVariant === 'brandDashboard' ? 1 : 2,
                        ...labelSx,
                    }}>
                    {label}
                </Typography>
            )}
            {isJuryDuty ? (
                <Box sx={juryDutyEditorSx}>
                    <ReactQuill
                        value={editorValue}
                        onChange={onChange}
                        modules={modules}
                        formats={formats}
                        theme="snow"
                        placeholder={placeholder}
                    />
                </Box>
            ) : (
                <ReactQuill
                    value={editorValue}
                    onChange={onChange}
                    modules={modules}
                    formats={formats}
                    theme="snow"
                    placeholder={placeholder}
                />
            )}
            {minChar &&
                (isJuryDuty ? (
                    <Box
                        component="div"
                        sx={{
                            flexShrink: 0,
                            flexGrow: 0,
                            px: 2,
                            py: compact ? 0.75 : 1.5,
                            minHeight: compact ? 28 : 40,
                            display: 'flex',
                            alignItems: 'center',
                            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                            bgcolor: '#1A1A1A',
                            borderRadius: '0 0 12px 12px',
                        }}>
                        <Typography
                            component="span"
                            sx={{
                                fontSize: 11,
                                lineHeight: 1.5,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                color: isMinCharMet
                                    ? theme[themeStyle].green_status_indicator_full
                                    : theme[themeStyle].primary_red,
                            }}>
                            Min char. {characterCount}/{minChar}
                        </Typography>
                    </Box>
                ) : (
                    <Typography
                        sx={{
                            fontSize: 11,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            mt: 1,
                            color: isMinCharMet
                                ? theme[themeStyle].green_status_indicator_full
                                : theme[themeStyle].primary_red,
                        }}>
                        Min char. {characterCount}/{minChar}
                    </Typography>
                ))}
        </Box>
    );
};
